import { store } from "store/actions/store";
import type { AppState } from "store/reducers/Reducer";

export type AuthSessionOwner = {
  generation: number;
  token: string;
  refreshToken: string;
  userId: string;
};

export class AuthSessionChangedError extends Error {
  constructor() {
    super("The signed-in session changed while the request was pending");
    this.name = "AuthSessionChangedError";
  }
}

export function captureAuthSession(): AuthSessionOwner {
  const auth = (store.getState() as AppState).auth;
  return {
    generation: auth.sessionGeneration ?? 0,
    token: auth.token ?? "",
    refreshToken: auth.refresh_token ?? "",
    userId: auth.user_id ?? "",
  };
}

/** Token rotation keeps its generation; sign-in and logout start a new one. */
export function isAuthSessionCurrent(owner: AuthSessionOwner): boolean {
  const current = captureAuthSession();
  return current.generation === owner.generation && current.userId === owner.userId;
}

export function isAuthSessionUnchanged(owner: AuthSessionOwner): boolean {
  const current = captureAuthSession();
  return (
    isAuthSessionCurrent(owner) &&
    current.token === owner.token &&
    current.refreshToken === owner.refreshToken
  );
}

export function assertAuthSessionUnchanged(owner: AuthSessionOwner): void {
  if (!isAuthSessionUnchanged(owner)) throw new AuthSessionChangedError();
}

/** Per-request hooks run before SDK response plugins, including MFA and redirects. */
export function authSessionFetchOptions(owner: AuthSessionOwner) {
  return {
    onRequest: () => assertAuthSessionUnchanged(owner),
    onSuccess: () => assertAuthSessionUnchanged(owner),
  };
}

let pendingRefresh: { owner: AuthSessionOwner; promise: Promise<string> } | undefined;

/** Share work across hook instances without sharing it with a later session. */
export function shareAuthSessionRefresh(
  owner: AuthSessionOwner,
  refresh: () => Promise<string>
): Promise<string> {
  if (
    pendingRefresh &&
    pendingRefresh.owner.generation === owner.generation &&
    pendingRefresh.owner.userId === owner.userId &&
    pendingRefresh.owner.refreshToken === owner.refreshToken
  ) {
    return pendingRefresh.promise;
  }

  const pending = { owner, promise: Promise.resolve().then(refresh) };
  pendingRefresh = pending;
  void pending.promise
    .finally(() => {
      if (pendingRefresh === pending) pendingRefresh = undefined;
    })
    .catch(() => undefined);
  return pending.promise;
}
